import React, { useState, useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setSelectedWorkspace } from "../../redux/workspace/actions";
import { UseCompanyWorkspaces } from "../../hooks/company";

import LoadingScreen from "../../components/LoadingScreen";
import SoftBox from "../../components/SoftBox";
import LightSelect from "./LightSelect";

import { formatNumber } from "../../helpers/formatter";
import { formatKpiPercent } from "../../helpers/scoreAggregate";

/* Darkened red → neutral → green scale for a performance ratio (gap-to-target:
   0 = on target, +0.5 = 50% ahead, -0.5 = 50% behind). */
const tintFor = (ratio) => {
  const r = Number(ratio) || 0;
  if (r > 0.5) return { bg: "#4e9c39", color: "#ffffff" };
  if (r > 0.15) return { bg: "#8cc76a", color: "#1f3d0b" };
  if (r >= -0.15) return { bg: "#e3e8ef", color: "#46536a" };
  if (r >= -0.5) return { bg: "#e8968c", color: "#5a1810" };
  return { bg: "#d15a4d", color: "#ffffff" };
};

const NO_DATA = { bg: "#f4f6f9", color: "#c2cbd8" };

// Attainment against target — 100% = on target ("% to target").
const attainment = (ratio) => `${Math.round((Number(ratio) || 0) * 100 + 100)}%`;

const statusFor = (ratio) => {
  const r = Number(ratio) || 0;
  if (r > 0.15) return "Ahead";
  if (r >= -0.15) return "On track";
  return "Behind";
};

const LEGEND = [
  { label: "Behind", bg: "#d15a4d" },
  { label: "On track", bg: "#e3e8ef" },
  { label: "Ahead", bg: "#4e9c39" },
];

const KpiTreemap = ({ matrix, weeklyMatrix }) => {
  const workspaces = UseCompanyWorkspaces(true);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [period, setPeriod] = useState("monthly");

  const activeMatrix = period === "monthly" ? matrix : weeklyMatrix;

  const boards = useMemo(
    () => (activeMatrix?.workspaces || []).filter((w) => w.metrics?.length),
    [activeMatrix],
  );
  const labels = activeMatrix?.labels || [];
  const subLabels = activeMatrix?.subLabels || null;
  const currentIndex =
    activeMatrix?.currentIndex !== undefined && activeMatrix?.currentIndex !== null
      ? activeMatrix.currentIndex
      : labels.length - 1;

  const [activeId, setActiveId] = useState(null);
  useEffect(() => {
    if (boards.length && !boards.some((b) => b.workspaceId === activeId)) {
      setActiveId(boards[0].workspaceId);
    }
  }, [boards, activeId]);

  const active = boards.find((b) => b.workspaceId === activeId) || boards[0];

  const options = useMemo(
    () => boards.map((b) => ({ value: b.workspaceId, label: b.workspace })),
    [boards],
  );

  // Auto-written summary of the active workspace's most recent period — reads
  // the current column (or the last one if nothing is in progress).
  const summary = useMemo(() => {
    const col = currentIndex >= 0 ? currentIndex : labels.length - 1;
    const items = (active?.metrics || [])
      .map((m) => {
        const cur = m.cells[col] || m.cells[m.cells.length - 1];
        return { title: m.title, ratio: cur?.ratio };
      })
      .filter((x) => x.ratio !== null && x.ratio !== undefined);
    if (!items.length) return null;
    const sorted = [...items].sort((a, b) => b.ratio - a.ratio);
    return {
      total: items.length,
      ahead: items.filter((x) => x.ratio > 0.15).length,
      strongest: sorted[0],
      weakest: sorted[sorted.length - 1],
    };
  }, [active, currentIndex, labels.length]);

  const [tip, setTip] = useState(null);
  const showTip = (e, metric, cell) => {
    const r = e.currentTarget.getBoundingClientRect();
    setTip({ metric, cell, x: Math.round(r.left + r.width / 2), y: Math.round(r.top) });
  };
  const hideTip = () => setTip(null);

  const goToDashboard = () => {
    const workspace = workspaces.find((w) => w._id === active?.workspaceId);
    if (!workspace) return;
    setTimeout(() => dispatch(setSelectedWorkspace({ selectedWorkspace: workspace })), 400);
    navigate("/dashboard");
  };

  const gridCols = `minmax(96px, 1.25fr) repeat(${labels.length}, 1fr)`;

  return (
    <>
    <SoftBox
      borderRadius="lg"
      sx={{
        p: "16px 20px 18px",
        minHeight: { xs: "360px", md: "400px", lg: "430px" },
        display: "flex",
        flexDirection: "column",
        background: "#ffffff",
        border: "1px solid #EAECF0",
      }}
    >
      {/* Card header — title + legend */}
      <SoftBox
        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}
      >
        <span style={{ fontFamily: "DM Sans", fontSize: "18px", fontWeight: 600, letterSpacing: "-0.01em", color: "#0F172A", lineHeight: 1.2 }}>
          Metric performance
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 12, fontSize: 10.5, color: "#98A2B3" }}>
          {LEGEND.map((l) => (
            <span key={l.label} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
              <i style={{ width: 10, height: 10, borderRadius: 3, background: l.bg, display: "inline-block" }} />
              {l.label}
            </span>
          ))}
        </span>
      </SoftBox>

      {!activeMatrix && (
        <SoftBox className="h-full w-full flex justify-center items-center">
          <LoadingScreen height="140px" logoHeight="140px" />
        </SoftBox>
      )}

      {activeMatrix && !boards.length && (
        <SoftBox className="h-full w-full flex justify-center items-center">
          <span style={{ fontFamily: "DM Sans", fontSize: 12.5, color: "#98A2B3" }}>No metric data yet.</span>
        </SoftBox>
      )}

      {activeMatrix && boards.length > 0 && active && (
        <>
          {/* Workspace switcher + period toggle */}
          <SoftBox sx={{ mt: 1.25, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
            <LightSelect
              options={options}
              value={active?.workspaceId}
              width={160}
              height={30}
              fontSize={12.5}
              radius={8}
              onChange={(opt) => setActiveId(opt.value)}
            />
            <div
              style={{
                display: "inline-flex",
                borderRadius: 8,
                border: "1px solid #EEF1F5",
                overflow: "hidden",
                flexShrink: 0,
              }}
            >
              {[
                { key: "monthly", label: "Monthly" },
                { key: "weekly", label: "Weekly" },
              ].map(({ key, label }, i) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setPeriod(key)}
                  style={{
                    padding: "0 11px",
                    height: 30,
                    fontSize: 12,
                    fontWeight: period === key ? 600 : 500,
                    fontFamily: "DM Sans",
                    background: period === key ? "var(--gmb-primary-soft, rgba(27,89,142,0.08))" : "#ffffff",
                    color: period === key ? "var(--gmb-primary, #1B598E)" : "#8A94A6",
                    border: "none",
                    borderRight: i === 0 ? "1px solid #EEF1F5" : "none",
                    cursor: "pointer",
                    transition: "background 120ms ease, color 120ms ease",
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </SoftBox>

          {/* Matrix — natural height so the card hugs its content (no dead
              space below), pushed down a bit so it isn't hugging the dropdown. */}
          <SoftBox
            sx={{
              display: "block",
              overflowX: "hidden",
              pt: "18px",
            }}
          >
            {/* Period header row — one or two lines (label + date range) */}
            <SoftBox
              sx={{ display: "grid", gridTemplateColumns: gridCols, columnGap: "6px", mb: "6px", alignItems: "end" }}
            >
              <span />
              {labels.map((m, i) => {
                const isCurrent = i === currentIndex;
                const accent = "var(--gmb-primary, #1B598E)";
                return (
                  <div key={m} style={{ textAlign: "center", lineHeight: 1.15 }}>
                    <div
                      style={{
                        fontFamily: "DM Sans",
                        fontSize: 10.5,
                        fontWeight: isCurrent ? 600 : 500,
                        color: isCurrent ? accent : "#98A2B3",
                      }}
                    >
                      {m}
                    </div>
                    {subLabels && subLabels[i] && (
                      <div
                        style={{
                          fontFamily: "DM Sans",
                          fontSize: 9,
                          fontWeight: 500,
                          color: isCurrent ? accent : "#B7BFCC",
                          marginTop: 1,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {subLabels[i]}
                        {isCurrent ? " · in progress" : ""}
                      </div>
                    )}
                  </div>
                );
              })}
            </SoftBox>

            {/* Metric rows */}
            <SoftBox sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              {active.metrics.map((metric) => (
                <SoftBox
                  key={metric.kpiId || metric.title}
                  sx={{ display: "grid", gridTemplateColumns: gridCols, columnGap: "6px" }}
                >
                  <span
                    onClick={goToDashboard}
                    title={metric.title}
                    style={{
                      fontFamily: "DM Sans", fontSize: 12, fontWeight: 500, color: "#344054",
                      display: "flex", alignItems: "center", cursor: "pointer",
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}
                  >
                    {metric.title}
                  </span>
                  {metric.cells.map((cell) => {
                    const has = cell.ratio !== null && cell.ratio !== undefined;
                    const t = has ? tintFor(cell.ratio) : NO_DATA;
                    return (
                      <div
                        key={`${metric.kpiId}-${cell.label}`}
                        onMouseEnter={(e) => showTip(e, metric, cell)}
                        onMouseLeave={hideTip}
                        style={{
                          background: t.bg,
                          color: t.color,
                          borderRadius: 8,
                          height: 34,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontFamily: "DM Sans",
                          fontSize: 12,
                          fontWeight: 600,
                          fontVariantNumeric: "tabular-nums",
                          cursor: "default",
                        }}
                      >
                        {has ? attainment(cell.ratio) : "–"}
                      </div>
                    );
                  })}
                </SoftBox>
              ))}
            </SoftBox>
          </SoftBox>

          {/* Auto-written insight — pinned to the bottom of the card at a
              constant height (mt:auto pushes it down), so it never resizes or
              shifts when switching between monthly and weekly. */}
          <SoftBox sx={{ mt: "auto", pt: "12px", flexShrink: 0 }}>
            {summary && (
              <SoftBox
                sx={{
                  minHeight: "40px",
                  borderRadius: "10px",
                  background: "#F8FAFB",
                  border: "1px solid #EEF1F5",
                  p: "10px 14px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <span style={{ fontFamily: "DM Sans", fontSize: 13, lineHeight: 1.5, color: "#667085" }}>
                  <b style={{ color: "#101828" }}>{summary.ahead} of {summary.total}</b>{" "}
                  metric{summary.total === 1 ? "" : "s"} ahead of target. Strongest is{" "}
                  <b style={{ color: "#101828" }}>{summary.strongest.title}</b> at{" "}
                  <b style={{ color: "#101828" }}>{attainment(summary.strongest.ratio)}</b>.
                  {summary.weakest.ratio < -0.15 && summary.weakest.title !== summary.strongest.title && (
                    <>
                      {" "}<b style={{ color: "#101828" }}>{summary.weakest.title}</b> is furthest behind at{" "}
                      <b style={{ color: "#101828" }}>{attainment(summary.weakest.ratio)}</b>.
                    </>
                  )}
                </span>
              </SoftBox>
            )}
          </SoftBox>
        </>
      )}
    </SoftBox>

      {/* Hover tooltip — portalled so the card's bounds never clip it */}
      {tip &&
        createPortal(
          <div
            style={{
              position: "fixed",
              left: tip.x,
              top: tip.y - 10,
              transform: "translate(-50%, -100%)",
              zIndex: 13000,
              pointerEvents: "none",
              background: "rgba(255,255,255,0.72)",
              backdropFilter: "blur(18px) saturate(160%)",
              WebkitBackdropFilter: "blur(18px) saturate(160%)",
              border: "1px solid rgba(255,255,255,0.9)",
              borderRadius: 12,
              boxShadow: "0 10px 30px rgba(16,24,40,0.16)",
              padding: "10px 12px",
              minWidth: 178,
              fontFamily: "DM Sans",
            }}
          >
            <div style={{ fontSize: 12.5, fontWeight: 600, color: "#101828" }}>{tip.metric.title}</div>
            <div style={{ fontSize: 11, color: "#98A2B3", marginTop: 1 }}>
              {tip.cell.fullLabel}
              {tip.cell.isCurrent ? " · in progress" : ""}
            </div>
            {tip.cell.ratio === null || tip.cell.ratio === undefined ? (
              <div style={{ fontSize: 12, color: "#667085", marginTop: 8 }}>No data for this month</div>
            ) : (
              <>
                <div style={{ height: 1, background: "#F2F4F7", margin: "8px 0" }} />
                <div style={{ display: "flex", justifyContent: "space-between", gap: 16, fontSize: 12, marginBottom: 3 }}>
                  <span style={{ color: "#667085" }}>Actual</span>
                  <span style={{ color: "#101828", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                    {tip.cell.kind === "%" ? formatKpiPercent(tip.metric, tip.cell.value) : formatNumber(tip.cell.value, tip.cell.kind)}
                  </span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 16, fontSize: 12, marginBottom: 6 }}>
                  <span style={{ color: "#667085" }}>{tip.cell.isCurrent ? "Target so far" : "Target"}</span>
                  <span style={{ color: "#101828", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                    {tip.cell.kind === "%" ? formatKpiPercent(tip.metric, tip.cell.target) : formatNumber(tip.cell.target, tip.cell.kind)}
                  </span>
                </div>
                <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12 }}>
                  <i style={{ width: 8, height: 8, borderRadius: 2, background: tintFor(tip.cell.ratio).bg, display: "inline-block" }} />
                  <span style={{ color: "#344054", fontWeight: 600 }}>
                    {attainment(tip.cell.ratio)} to target
                  </span>
                  <span style={{ color: "#98A2B3" }}>· {statusFor(tip.cell.ratio)}</span>
                </div>
              </>
            )}
          </div>,
          document.body,
        )}
    </>
  );
};

export default KpiTreemap;
